import React from 'react';
import { pillars, stats } from '../data/mock';
import useReveal from '../hooks/useReveal';

const About = () => {
  const imgReveal = useReveal();
  const contentReveal = useReveal();

  return (
    <section id="about" data-testid="about-section" className="relative py-28 md:py-36 bg-carbon overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          {/* Left: Brand Crest — onyx luxury composition */}
          <div
            ref={imgReveal.ref}
            className={`reveal ${imgReveal.visible ? 'is-visible' : ''} lg:col-span-5 relative`}
          >
            <div
              data-testid="about-brand-crest"
              className="relative aspect-[4/5] rounded-sm overflow-hidden border border-pewter/60 bg-onyx flex items-center justify-center"
            >
              {/* Soft blush radial glow */}
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle at 50% 38%, rgba(236,138,174,0.14) 0%, transparent 65%)',
                }}
              />

              {/* Subtle grain veil */}
              <div
                aria-hidden="true"
                className="absolute inset-0 grain pointer-events-none opacity-60"
              />

              {/* Crest content */}
              <div className="relative flex flex-col items-center text-center px-8">
                {/* Top hairline ornament */}
                <div className="flex items-center gap-3 mb-10">
                  <div className="h-px w-10 bg-platinum/40" />
                  <div className="w-1 h-1 rounded-full bg-blush/70" />
                  <div className="h-px w-10 bg-platinum/40" />
                </div>

                {/* Logo */}
                <img
                  src="/logo.png"
                  alt="Snatched Beauties Privé"
                  className="w-44 md:w-52 lg:w-56 h-auto object-contain mb-8"
                  style={{ filter: 'drop-shadow(0 12px 32px rgba(236,138,174,0.18))' }}
                />

                {/* Wordmark */}
                <p className="font-serif text-ivory text-xl md:text-2xl leading-tight tracking-wide">
                  Snatched Beauties{' '}
                  <span className="brand-italic chrome-rose">Privé</span>
                </p>

                {/* Tagline */}
                <p
                  className="text-platinum text-[10px] md:text-xs tracking-[0.4em] uppercase mt-4"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Est. Los Angeles · 2021
                </p>

                {/* Bottom hairline ornament */}
                <div className="flex items-center gap-3 mt-10">
                  <div className="h-px w-10 bg-platinum/40" />
                  <div className="w-1 h-1 rounded-full bg-blush/70" />
                  <div className="h-px w-10 bg-platinum/40" />
                </div>
              </div>
            </div>
            <div className="hidden md:block absolute -bottom-8 -right-8 bg-onyx border border-pewter/70 p-6 w-60">
              <p className="eyebrow mb-3">Est. Los Angeles</p>
              <p className="font-serif text-ivory text-lg leading-snug">
                A curated refuge for <span className="brand-italic chrome-rose">visible results</span>.
              </p>
            </div>
          </div>

          {/* Right: Content */}
          <div
            ref={contentReveal.ref}
            className={`reveal ${contentReveal.visible ? 'is-visible' : ''} lg:col-span-7`}
            style={{ '--reveal-delay': '120ms' }}
          >
            <p className="eyebrow mb-6">
              <span className="inline-block w-8 h-px bg-silverx align-middle mr-3" />
              The Studio
            </p>
            <h2 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-[56px] leading-[1.06] mb-10">
              Welcome to Snatched Beauties{' '}
              <span className="brand-italic chrome-rose">Privé</span>
            </h2>
            <div className="space-y-6 text-platinum text-base md:text-lg leading-relaxed max-w-2xl">
              <p>
                At Snatched Beauties Privé, we specialize in luxury non-invasive aesthetic
                and wellness treatments designed to help you look and feel your best with
                intention. Our customized protocols combine body contouring, lymphatic
                therapies, advanced wellness solutions, and medical weight optimization to
                create a treatment experience tailored to your individual goals.
              </p>
              <p className="text-ivory">
                We believe transformation should be{' '}
                <span className="brand-italic chrome-rose">strategic, elevated, and never one-size-fits-all.</span>
              </p>
            </div>

            {/* Pillars */}
            <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-6">
              {pillars.map((p, idx) => (
                <div key={p.title} className="flex gap-4">
                  <span className="font-serif chrome-rose text-lg leading-none pt-1">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <p className="font-serif text-ivory text-lg mb-1">{p.title}</p>
                    <p className="text-silverx text-sm leading-relaxed">{p.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="hairline my-14" />
            <div className="grid grid-cols-3 gap-6" data-testid="about-stats">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-serif text-ivory text-3xl md:text-4xl">{s.value}</p>
                  <p className="text-silverx text-[11px] tracking-[0.22em] uppercase mt-2">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
